import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Layout } from '@/components/layout'
import { integrationService, type IntegrationSettings } from '@/lib/services/integrationService'
import { useToast } from '@/lib/hooks/use-toast'
import {
  Settings,
  CheckCircle,
  XCircle,
  RefreshCw,
  Edit,
  Trash2,
  Power,
  TestTube,
} from 'lucide-react'
import SharePointLogo from '@/assets/sharepoint.svg'
import CoostoLogo from '@/assets/coosto.svg'
import { SharePointConfigDialog } from './components/integration/SharePointConfigDialog'
import { CoostoConfigDialog } from './components/integration/CoostoConfigDialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

export function IntegrationManagementPage() {
  const { t } = useTranslation()
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const [editingIntegration, setEditingIntegration] = useState<string | null>(null)
  const [deletingIntegration, setDeletingIntegration] = useState<string | null>(null)

  // Fetch all integrations
  const { data: integrations, isLoading } = useQuery({
    queryKey: ['integrations'],
    queryFn: () => integrationService.listIntegrations(),
  })

  // Toggle integration active status
  const toggleMutation = useMutation({
    mutationFn: ({ type, isActive }: { type: string; isActive: boolean }) =>
      integrationService.toggleIntegration(type, isActive),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] })
      toast({
        title: t('integration.toggled'),
        description: t('integration.toggle_success'),
      })
    },
    onError: () => {
      toast({
        title: t('common.error'),
        description: t('integration.toggle_error'),
        variant: 'destructive',
      })
    },
  })

  // Test integration
  const testMutation = useMutation({
    mutationFn: (type: string) => integrationService.testIntegration(type),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] })
      toast({
        title: t('integration.test_complete'),
        description: data.message,
        variant: data.status === 'success' ? 'default' : 'destructive',
      })
    },
    onError: () => {
      toast({
        title: t('common.error'),
        description: t('integration.test_error'),
        variant: 'destructive',
      })
    },
  })

  // Delete integration
  const deleteMutation = useMutation({
    mutationFn: (type: string) => integrationService.deleteIntegration(type),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] })
      setDeletingIntegration(null)
      toast({
        title: t('integration.deleted'),
        description: t('integration.delete_success'),
      })
    },
    onError: () => {
      toast({
        title: t('common.error'),
        description: t('integration.delete_error'),
        variant: 'destructive',
      })
    },
  })

  const handleToggle = (type: string, currentStatus: boolean) => {
    toggleMutation.mutate({ type, isActive: !currentStatus })
  }

  const handleTest = (type: string) => {
    testMutation.mutate(type)
  }

  const handleEdit = (type: string) => {
    setEditingIntegration(type)
  }

  const handleDelete = (type: string) => {
    setDeletingIntegration(type)
  }

  const confirmDelete = () => {
    if (deletingIntegration) {
      deleteMutation.mutate(deletingIntegration)
    }
  }

  const getStatusBadge = (integration: IntegrationSettings) => {
    if (!integration.isConfigured) {
      return <Badge variant="outline">{t('integration.not_configured')}</Badge>
    }
    if (!integration.isActive) {
      return <Badge variant="secondary">{t('integration.inactive')}</Badge>
    }
    return <Badge variant="default" className="bg-green-600 text-white">{t('integration.active')}</Badge>
  }

  const getTestStatusIcon = (status: string | null) => {
    if (!status) return null
    if (status === 'success') {
      return <CheckCircle className="h-4 w-4 text-green-600" />
    }
    return <XCircle className="h-4 w-4 text-red-600" />
  }

  return (
    <Layout>
      {/* Header */}
      <div className="border-b bg-muted/30">
        <div className="container mx-auto px-6 py-6">
          <div className="flex-column md:flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold tracking-tight text-foreground flex items-center space-x-2">
                <Settings className="h-8 w-8" />
                <span>{t('integration.title')}</span>
              </h1>
              <p className="text-muted-foreground mt-2">{t('integration.description')}</p>
            </div>
            <Button
              onClick={() => queryClient.invalidateQueries({ queryKey: ['integrations'] })}
              variant="secondary"
              size="sm"
              className="mt-4 md:mt-0"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              {t('common.refresh')}
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-8 space-y-6">
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        )}

        {!isLoading && integrations && integrations.length === 0 && (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground">{t('integration.no_integrations')}</p>
            </CardContent>
          </Card>
        )}

        {!isLoading && integrations && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* SharePoint Integration */}
            {!integrations.find(i => i.integrationType === 'sharepoint') && (
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-3 mb-2">
                    <img src={SharePointLogo} alt="SharePoint" className="h-8 w-8" />
                    <CardTitle>SharePoint</CardTitle>
                  </div>
                  <CardDescription>Microsoft SharePoint document integration</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button variant="gradient" onClick={() => handleEdit('sharepoint')} className="w-full">
                    <Settings className="h-4 w-4 mr-2" />
                    {t('integration.configure')}
                  </Button>
                </CardContent>
              </Card>
            )}

            {integrations
              .filter(i => i.integrationType === 'sharepoint')
              .map(integration => (
                <Card key={integration.id}>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <img src={SharePointLogo} alt="SharePoint" className="h-8 w-8 mt-1 flex-shrink-0" />
                        <div>
                          <CardTitle className="flex items-center space-x-2">
                            <span>{integration.name}</span>
                            {getStatusBadge(integration)}
                          </CardTitle>
                          <CardDescription className="mt-2">
                            {integration.description}
                          </CardDescription>
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Test Status */}
                    {integration.lastTestedAt && (
                      <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                        {getTestStatusIcon(integration.lastTestStatus)}
                        <span>
                          {t('integration.last_tested')}:{' '}
                          {new Date(integration.lastTestedAt).toLocaleString()}
                        </span>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex flex-wrap gap-2">
                      <Button
                        onClick={() => handleEdit(integration.integrationType)}
                        variant="outline"
                        size="sm"
                      >
                        <Edit className="h-4 w-4 mr-2" />
                        {t('common.edit')}
                      </Button>

                      <Button
                        onClick={() => handleToggle(integration.integrationType, integration.isActive)}
                        variant="outline"
                        size="sm"
                      >
                        <Power className="h-4 w-4 mr-2" />
                        {integration.isActive ? t('common.disable') : t('common.enable')}
                      </Button>

                      <Button
                        onClick={() => handleTest(integration.integrationType)}
                        variant="outline"
                        size="sm"
                        disabled={testMutation.isPending}
                      >
                        <TestTube className="h-4 w-4 mr-2" />
                        {t('integration.test')}
                      </Button>

                      <Button
                        onClick={() => handleDelete(integration.integrationType)}
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        {t('common.delete')}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}

            {/* Coosto Integration */}
            {!integrations.find(i => i.integrationType === 'coosto') && (
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-3 mb-2">
                    <img src={CoostoLogo} alt="Coosto" className="h-8 w-8" />
                    <CardTitle>Coosto</CardTitle>
                  </div>
                  <CardDescription>Coosto media monitoring integration</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button variant="gradient" onClick={() => handleEdit('coosto')} className="w-full">
                    <Settings className="h-4 w-4 mr-2" />
                    {t('integration.configure')}
                  </Button>
                </CardContent>
              </Card>
            )}

            {integrations
              .filter(i => i.integrationType === 'coosto')
              .map(integration => (
                <Card key={integration.id}>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <img src={CoostoLogo} alt="Coosto" className="h-8 w-8 mt-1 flex-shrink-0" />
                        <div>
                          <CardTitle className="flex items-center space-x-2">
                            <span>{integration.name}</span>
                            {getStatusBadge(integration)}
                          </CardTitle>
                          <CardDescription className="mt-2">
                            {integration.description}
                          </CardDescription>
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Test Status */}
                    {integration.lastTestedAt && (
                      <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                        {getTestStatusIcon(integration.lastTestStatus)}
                        <span>
                          {t('integration.last_tested')}:{' '}
                          {new Date(integration.lastTestedAt).toLocaleString()}
                        </span>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex flex-wrap gap-2">
                      <Button
                        onClick={() => handleEdit(integration.integrationType)}
                        variant="outline"
                        size="sm"
                      >
                        <Edit className="h-4 w-4 mr-2" />
                        {t('common.edit')}
                      </Button>

                      <Button
                        onClick={() => handleToggle(integration.integrationType, integration.isActive)}
                        variant="outline"
                        size="sm"
                      >
                        <Power className="h-4 w-4 mr-2" />
                        {integration.isActive ? t('common.disable') : t('common.enable')}
                      </Button>

                      <Button
                        onClick={() => handleTest(integration.integrationType)}
                        variant="outline"
                        size="sm"
                        disabled={testMutation.isPending}
                      >
                        <TestTube className="h-4 w-4 mr-2" />
                        {t('integration.test')}
                      </Button>

                      <Button
                        onClick={() => handleDelete(integration.integrationType)}
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        {t('common.delete')}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
          </div>
        )}
      </div>

      {/* Configuration Dialogs */}
      {editingIntegration === 'sharepoint' && (
        <SharePointConfigDialog
          open={true}
          onClose={() => setEditingIntegration(null)}
          onSuccess={() => {
            setEditingIntegration(null)
            queryClient.invalidateQueries({ queryKey: ['integrations'] })
          }}
        />
      )}

      {editingIntegration === 'coosto' && (
        <CoostoConfigDialog
          open={true}
          onClose={() => setEditingIntegration(null)}
          onSuccess={() => {
            setEditingIntegration(null)
            queryClient.invalidateQueries({ queryKey: ['integrations'] })
          }}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deletingIntegration !== null} onOpenChange={() => setDeletingIntegration(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('integration.delete_confirm_title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('integration.delete_confirm_message')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  )
}
